import React, { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import type { CityRequestGroup } from '@/pages/ArtistWaitlistPage'
import { format } from 'date-fns'

interface WaitlistCityListProps {
  cityGroups: CityRequestGroup[]
}

// Determine color based on confidence level (1-7: red to green)
const getConfidenceColor = (level: number): string => {
  switch (level) {
    case 1: return 'bg-red-500'
    case 2: return 'bg-orange-500'
    case 3: return 'bg-amber-500'
    case 4: return 'bg-yellow-500'
    case 5: return 'bg-lime-500'
    case 6: return 'bg-green-500'
    case 7: return 'bg-green-600'
    default: return 'bg-gray-300 dark:bg-gray-600'
  }
}

// Determine discrete color based on 4 quartiles: Red, Yellow, Orange, Green
const getCircleColor = (percentage: number): string => {
  if (percentage <= 0.25) return 'rgb(239, 68, 68)' // red-500
  if (percentage <= 0.5) return 'rgb(234, 179, 8)' // yellow-500
  if (percentage <= 0.75) return 'rgb(234, 88, 12)' // orange-500
  return 'rgb(34, 197, 94)' // green-500
}

function CityGroupItem({ 
  cityGroup, 
  maxCount 
}: { 
  cityGroup: CityRequestGroup
  maxCount: number 
}) {
  const [isExpanded, setIsExpanded] = useState(false)

  const averageConfidence = (
    cityGroup.requests.reduce((sum, r) => sum + r.confidenceLevel, 0) / cityGroup.requests.length
  ).toFixed(1)

  // Calculate fill percentage (0 to 1), with minimum of 10% for visibility
  const fillPercentage = Math.max(0.1, cityGroup.count / maxCount)

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      {/* City Header - Clickable */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3 flex-1">
          {/* Circular indicator with request count */}
          <div className="flex-shrink-0 relative" style={{ width: '48px', height: '48px' }}>
            <svg className="transform -rotate-90" width="48" height="48">
              {/* Background circle */}
              <circle
                cx="24"
                cy="24"
                r="18"
                fill="none"
                stroke="#e5e5e5"
                strokeWidth="6"
              />
              {/* Progress circle */}
              <circle
                cx="24"
                cy="24"
                r="18"
                fill="none"
                stroke={getCircleColor(fillPercentage)}
                strokeWidth="6"
                strokeDasharray={`${fillPercentage * 113.1} 113.1`}
                strokeLinecap="round"
                className="transition-all duration-300"
              />
            </svg>
            {/* Request count in center */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-bold text-foreground">{cityGroup.count}</span>
            </div>
          </div>
          
            <div className="flex-1 text-left">
            <h3 className="font-semibold text-base">
              {cityGroup.city}, {cityGroup.country}
            </h3>
            
            {(() => {
              const flashCount = cityGroup.requests.filter(r => r.isFlash).length
              const customCount = cityGroup.requests.filter(r => !r.isFlash).length
              
              return (
                <div className="flex items-center gap-3 mt-1 text-xs">
                  {/* Desktop view: show flash/custom breakdown */}
                  <span className="hidden sm:block text-muted-foreground">
                    <span className="text-foreground font-medium">{flashCount}</span> {flashCount === 1 ? 'flash' : 'flashes'}{' '}
                    <span className="text-foreground font-medium">{customCount}</span> {customCount === 1 ? 'custom' : 'custom'}
                  </span>
                  
                  {/* Mobile view: show only avg confidence */}
                  <span className="sm:hidden flex items-center gap-2">
                    <span className="text-foreground font-medium">{averageConfidence}</span>
                    <span className="text-muted-foreground">avg. confidence</span>
                  </span>
                  
                  {/* Desktop view: show avg confidence */}
                  <span className="hidden sm:flex items-center gap-2">
                    <span className="text-foreground font-medium">{averageConfidence}</span>
                    <span className="text-muted-foreground">avg. confidence</span>
                  </span>
                </div>
              )
            })()}
          </div>
        </div>

        <div className="ml-2">
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Expanded Requests List */}
      {isExpanded && (
        <div className="border-t border-border bg-background">
          {/* Desktop: Grid layout */}
          <div className="hidden sm:grid sm:grid-cols-[auto_auto_1fr_auto]">
            {cityGroup.requests
              .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
              .map((request) => {
                const requestType = request.isFlash ? 'Flash' : 'Custom'
                const descriptionText = request.customIdea || ''
                
                return (
                  <React.Fragment key={request.id}>
                    {/* Confidence level indicator */}
                    <div className="flex items-center justify-center gap-0.5 py-3 px-3 border-b border-border">
                      {[...Array(7)].map((_, index) => {
                        const isFilled = index < request.confidenceLevel
                        return (
                          <div
                            key={index}
                            className={`w-1 h-4 rounded-sm transition-colors ${
                              isFilled ? getConfidenceColor(request.confidenceLevel) : 'bg-gray-200'
                            }`}
                          />
                        )
                      })}
                    </div>
                    
                    {/* Type column */}
                    <p className="text-sm text-foreground whitespace-nowrap py-3 px-3 border-b border-border">
                      {requestType}
                    </p>
                    
                    {/* Description column */}
                    <p className="text-sm text-muted-foreground truncate py-3 px-3 border-b border-border">
                      {descriptionText}
                    </p>
                    
                    {/* Date column */}
                    <span className="text-xs text-muted-foreground whitespace-nowrap text-right py-3 px-3 border-b border-border">
                      {format(request.createdAt, 'MMM d, yyyy')}
                    </span>
                  </React.Fragment>
                )
              })}
          </div>

          {/* Mobile: Stacked layout with two-row structure when description exists */}
          <div className="sm:hidden">
            {cityGroup.requests
              .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
              .map((request) => {
                const requestType = request.isFlash ? 'Flash' : 'Custom'
                const descriptionText = request.customIdea || ''
                const hasDescription = descriptionText.trim().length > 0
                
                return (
                  <div key={request.id} className="border-b border-border">
                    {/* Main row */}
                    <div className="grid grid-cols-[auto_auto_1fr] gap-3 py-3 px-3">
                      {/* Confidence level indicator */}
                      <div className="flex items-center justify-center gap-0.5">
                        {[...Array(7)].map((_, index) => {
                          const isFilled = index < request.confidenceLevel
                          return (
                            <div
                              key={index}
                              className={`w-1 h-4 rounded-sm transition-colors ${
                                isFilled ? getConfidenceColor(request.confidenceLevel) : 'bg-gray-200'
                              }`}
                            />
                          )
                        })}
                      </div>
                      
                      {/* Type */}
                      <p className="text-sm text-foreground whitespace-nowrap">
                        {requestType}
                      </p>
                      
                      {/* Date */}
                      <span className="text-xs text-muted-foreground whitespace-nowrap text-right">
                        {format(request.createdAt, 'MMM d, yyyy')}
                      </span>
                    </div>

                    {/* Description row (only if description exists) */}
                    {hasDescription && (
                      <div className="px-3 pb-3 pt-0">
                        <p className="text-sm text-muted-foreground">
                          {descriptionText}
                        </p>
                      </div>
                    )}
                  </div>
                )
              })}
          </div>
        </div>
      )}
    </div>
  )
}

export function WaitlistCityList({ cityGroups }: WaitlistCityListProps) {
  if (cityGroups.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <p className="text-muted-foreground text-center">
          No requests in this region
        </p>
      </div>
    )
  }

  // Find the max count to calculate bar proportions
  const maxCount = Math.max(...cityGroups.map(g => g.count), 1)

  return (
    <div className="phone-container-padded pb-4 space-y-3">
      {cityGroups.map((cityGroup) => (
        <CityGroupItem 
          key={`${cityGroup.city}-${cityGroup.country}`} 
          cityGroup={cityGroup}
          maxCount={maxCount}
        />
      ))}
    </div>
  )
}

